<template>
  <div
    id="bubblesContainer"
    ref="bubblesContainer"
    class="mh-100 m-0 flex-grow-1 mobile-bubbles-container bg-white"
    @touchstart="hideTouchableIndicator"
  >
    <BubblesCanvas />
    <BubblesHeaders />
    <div data-test="data-visualisation-legends" class="position-legends-top-left">
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === TOP_LEFT" />
    </div>
    <div data-test="data-visualisation-legends" class="position-legends-bottom-left">
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === BOTTOM_LEFT" />
    </div>
    <div data-test="data-visualisation-legends" class="position-legends-bottom-right">
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === BOTTOM_RIGHT" />
    </div>
    <Teleport to="body">
      <MobileBubblePopup />
    </Teleport>
    <div id="bubblesMinSizeSquare" ref="bubblesMinSizeSquare" />
    <transition name="fade">
      <b-img
        v-if="showTouchableIndicator"
        fluid
        :src="TouchableImage"
        class="touchable-indicator pulse"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import BubblesCanvas from '@/components/public/Visualisations/Bubbles/BubblesCanvas.vue'
import BubblesHandles from '@/components/public/Visualisations/Bubbles/Handles/BubblesHandles.vue'
import BubblesHeaders from '@/components/public/Visualisations/Bubbles/Headers/BubblesHeaders.vue'
import MobileBubblePopup from '@/components/public/Popup/MobileBubblePopup.vue'
import FilterBubblesUI from '@/components/public/FilterByCategory/FilterBubblesUI.vue'
import BubblesLegendsAccordion from '@/components/public/BubblesLegendsAccordion.vue'
import TouchableImage from '@/assets/imgs/touchable.svg'
import Teleport from 'vue2-teleport' // TODO: Remove this import and component when migrating to vue3

import { LegendPositions } from '@/types'

const components = {
  BubblesCanvas,
  MobileBubblePopup,
  BubblesHandles,
  BubblesHeaders,
  FilterBubblesUI,
  BubblesLegendsAccordion,
  Teleport,
}

// eslint-disable-next-line no-use-before-define
@Component<MobileBubbles>({
  components,
  watch: {
    storyPath() {
      this.hideTouchableIndicator()
    },
  },
})
export default class MobileBubbles extends Vue {
  TOP_LEFT = LegendPositions.TOP_LEFT
  BOTTOM_LEFT = LegendPositions.BOTTOM_LEFT
  BOTTOM_RIGHT = LegendPositions.BOTTOM_RIGHT
  TouchableImage = TouchableImage
  showTouchableIndicator = true
  resizeObserver: ResizeObserver | null = null

  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeBubblesMinSizeSquare()
      this.$typedStore.public.size.updateBubblesContainerDimensions()
      this.$typedStore.public.size.updateBubblesMinSizeSquareOffset()
      this.$typedStore.public.size.updateBubblesVisualisationScaleFactor()
    })
    this.resizeObserver.observe(this.$el as HTMLElement)
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  resizeBubblesMinSizeSquare() {
    const size = Math.min(this.bubblesContainer.clientWidth, this.bubblesContainer.clientHeight)
    this.bubblesMinSizeSquare.style.width = `${size}px`
    this.bubblesMinSizeSquare.style.height = `${size}px`
  }

  get bubblesContainer() {
    return this.$refs.bubblesContainer! as HTMLDivElement
  }

  get bubblesMinSizeSquare() {
    return this.$refs.bubblesMinSizeSquare! as HTMLDivElement
  }

  get filterByCategoryPosition() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.filterByCategory
      .position
  }

  get bubblesLegendsPosition() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.bubblesLegends
      .legendPosition
  }

  get storyPath() {
    return this.$typedStore.visualisationData.storyPath
  }

  hideTouchableIndicator() {
    this.showTouchableIndicator = false
  }
}
</script>

<style scoped>
.mobile-bubbles-container {
  position: relative;
  /* use a constant scale for the Bubbles visualisation */
  transform: scale(1);
  /* allow the popups to display on top*/
  z-index: 2;
}

#bubblesMinSizeSquare {
  background-color: rgba(0, 0, 0, 0);
  pointer-events: none;

  /* Center the div */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.position-legends-top-left {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 0.5rem;
  margin-left: 0.5rem;
}

.position-legends-bottom-left {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
}

.position-legends-bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
}

.touchable-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 17%;
  transform: translate(-50%, -50%);
  opacity: 0.95;
  pointer-events: none;
  z-index: 1000;
}

.fade-leave-active {
  transition: opacity 0.2s ease;
  animation: none !important;
}

.fade-leave-to {
  opacity: 0;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.95;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.04);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.95;
  }
}

.pulse {
  animation: pulse 1.1s infinite;
}
</style>
